import { supabase } from '@/integrations/supabase/client';
import type { 
  MediaOutlet, 
  MediaOutletInsert, 
  MediaOutletUpdate,
  MediaOutletFilters,
  OutletOperationResult 
} from '@/types/mediaOutlets';

export const mediaOutletService = {
  async getAllOutlets(filters?: MediaOutletFilters): Promise<MediaOutlet[]> {
    try {
      let query = supabase
        .from('media_outlets')
        .select('*')
        .order('display_order', { ascending: true });
      
      if (filters?.is_active !== undefined) {
        query = query.eq('is_active', filters.is_active);
      }
      
      if (filters?.category) {
        query = query.eq('outlet_category', filters.category);
      }
      
      if (filters?.is_popular) {
        query = query.eq('is_popular', true);
      }
      
      if (filters?.search) {
        query = query.ilike('outlet_name', `%${filters.search}%`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching outlets:', error);
      return [];
    }
  },

  async getPopularOutlets(): Promise<MediaOutlet[]> {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .select('*')
        .eq('is_popular', true)
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching popular outlets:', error);
      return [];
    }
  },

  async getOutletsByCategory(category: string): Promise<MediaOutlet[]> {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .select('*')
        .eq('outlet_category', category)
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching outlets by category:', error);
      return [];
    }
  },

  async searchOutlets(query: string): Promise<MediaOutlet[]> {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .select('*')
        .ilike('outlet_name', `%${query}%`)
        .eq('is_active', true)
        .order('usage_count', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching outlets:', error);
      return [];
    }
  },

  async getOutletById(id: string): Promise<MediaOutlet | null> {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching outlet by ID:', error);
      return null;
    }
  },

  async createOutlet(outlet: MediaOutletInsert): Promise<OutletOperationResult> {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .insert(outlet)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('Media outlet created successfully:', data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error creating outlet:', error);
      return { success: false, error: error.message };
    }
  },

  async updateOutlet(id: string, updates: MediaOutletUpdate): Promise<OutletOperationResult> {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('Media outlet updated successfully:', data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error updating outlet:', error);
      return { success: false, error: error.message };
    }
  },

  async deleteOutlet(id: string): Promise<OutletOperationResult> {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .update({ is_active: false })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      console.log('Media outlet deactivated successfully:', data);
      return { success: true, data };
    } catch (error: any) {
      console.error('Error deactivating outlet:', error);
      return { success: false, error: error.message };
    }
  },

  async reorderOutlets(orderedIds: string[]): Promise<OutletOperationResult> {
    try {
      const updates = orderedIds.map((id, index) => 
        supabase
          .from('media_outlets')
          .update({ display_order: index })
          .eq('id', id)
      );
      
      await Promise.all(updates);
      
      console.log('Media outlets reordered successfully');
      return { success: true };
    } catch (error: any) {
      console.error('Error reordering outlets:', error);
      return { success: false, error: error.message };
    }
  },

  async incrementUsageCount(outletId: string): Promise<void> {
    try {
      // Get current count and increment
      const { data: outlet } = await supabase
        .from('media_outlets')
        .select('usage_count')
        .eq('id', outletId)
        .single();
      
      if (outlet) {
        await supabase
          .from('media_outlets')
          .update({ usage_count: (outlet.usage_count || 0) + 1 })
          .eq('id', outletId);
      }
      
      console.log('Outlet usage count incremented:', outletId);
    } catch (error) {
      console.error('Error incrementing usage count:', error);
    }
  },

  async getOutletStats(outletId: string): Promise<{ usage_count: number } | null> {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .select('usage_count')
        .eq('id', outletId)
        .single();
      
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching outlet stats:', error);
      return null;
    }
  }
};
